import { useAuth0 } from '@auth0/auth0-react';
import { AppError } from 'Components/AppError';
import { AppLoader } from 'Components/AppLoader';
import {
  AUTH0_MANAGEMENT_API_SCOPE,
  IS_ELECTRON,
} from 'Constants/env';
import { AppWithErrorHandler } from 'Containers/App';
import { DesktopWelcome } from 'Containers/DesktopWelcome';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { reaction } from 'mobx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Grid } from 'semantic-ui-react';
import { setupAppLifecycleLogging } from 'Utils/appLifecycleLogger';
import { downloadDesktopApp } from 'Utils/globalUserActions';
import { handleAuth0CodeReceived } from 'Utils/ipcRendererEvents';
import { getLogger } from 'Utils/logger';
import { isBlockingUpdateRequired } from 'Utils/updateVersionGate';
import { IMainApp } from './interfaces';

const CenteredLayout = ({ children }: { children: React.ReactNode }) => (
  <Grid textAlign="center" verticalAlign="middle" className="login">
    <Grid.Column style={{ maxWidth: 450 }} className="login-container">
      {children}
    </Grid.Column>
  </Grid>
);

const UpdateRequiredMessage = () => (
  <>
    <p>
      The current version of the Communicator is outdated. Please update to the
      latest one:
    </p>
    <a href="javascript:void(0)" onClick={downloadDesktopApp}>
      Download here the latest version
    </a>
    <p className="app-notice-message">
      Your version of Communicator needs a manual update to continue working.
      Please download the new version to continue using it. If you need further
      help from our support colleagues, open a ticket here{' '}
      <a
        href="https://www.broadvoice.com/contact-us/"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://www.broadvoice.com/contact-us/
      </a>
    </p>
  </>
);

export const MainApp = ({ rootStore }: IMainApp) => {
  const auth0 = useAuth0();
  const ldClient = useLDClient();
  const { personStore } = rootStore;
  const [loading, setLoading] = useState<boolean>(true);
  const [shouldDisplayDesktopWelcome, setShouldDisplayDesktopWelcome] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const prevIsAuthenticatedRef = useRef<boolean | null>(null);

  useEffect(() => {
    getLogger();
  }, []);

  useEffect(() => {
    if (!personStore.loggedInAccountId) return;

    const disposer = reaction(
      () => rootStore.configStore.signedInPersonConfigLoaded,
      (loaded) => {
        if (loaded) {
          void (async () => {
            const signedInPersonConfig = await rootStore.configStore
              .signedInPersonConfig;

            const shouldLogMessageCreated =
              signedInPersonConfig?.data?.feature?.logMessageCreated?.enabled;

            if (shouldLogMessageCreated) {
              const cleanup = setupAppLifecycleLogging();
              return cleanup;
            }
          })();
        }
      }
    );

    return () => disposer();
  }, [
    personStore.loggedInAccountId,
    rootStore.configStore.signedInPersonConfig,
    rootStore.configStore.signedInPersonConfigLoaded,
  ]);

  useEffect(() => {
    personStore.setAuth0(auth0);
    personStore.setLDClient(ldClient);
  }, [auth0, ldClient, personStore]);

  useEffect(() => {
    if (auth0.isLoading) {
      return;
    }

    const prev = prevIsAuthenticatedRef.current;
    const current = auth0.isAuthenticated;

    const lostAuth =
      prev === true &&
      current === false &&
      personStore.IsLoggedIn &&
      !personStore.isLoggingOut;

    if (lostAuth) {
      void personStore.initiateAutoLogout(true);
    }

    prevIsAuthenticatedRef.current = current;
  }, [
    auth0.isAuthenticated,
    auth0.isLoading,
    personStore.IsLoggedIn,
    personStore.isLoggingOut,
  ]);

  /**
   * Login with token from BHive API.
   * If the login is for desktop startup refresh, we don't want to show the error message, instead we just show the desktop welcome screen.
   * @param isDesktopStartupRefresh - Whether the login is for desktop startup refresh.
   */
  const loginWithToken = useCallback(
    async (isDesktopStartupRefresh = false) => {
      try {
        const token = await personStore.exchangeForBVToken({
          redirect_uri: window.location.href,
          ...(isDesktopStartupRefresh && {
            suppressLogoutOnAuthFailure: true,
          }),
        });
        await personStore.loginWithExistingToken(token);
      } catch (e) {
        if (isDesktopStartupRefresh) {
          setShouldDisplayDesktopWelcome(true);
        } else {
          setError(e.response.data.error);
        }
      } finally {
        setLoading(false);
      }
    },
    [auth0, personStore]
  );

  const loginWithUrl = useCallback(
    async (url: string) => {
      if (auth0.isAuthenticated) {
        return;
      }

      setShouldDisplayDesktopWelcome(false);

      try {
        await auth0.handleRedirectCallback(decodeURIComponent(url));
        await loginWithToken();
      } catch (error) {
        console.error(error);
        setShouldDisplayDesktopWelcome(true);
      }
    },
    [auth0, loginWithToken]
  );

  useEffect(() => {
    const cleanup = handleAuth0CodeReceived((_, url: string) => {
      void loginWithUrl(url);
    });

    return () => cleanup?.();
  }, [auth0.isAuthenticated, loginWithUrl]);

  useEffect(() => {
    //short circuit if update is required, do not prompt user to login
    if (isBlockingUpdateRequired()) {
      return;
    }

    if (auth0.isLoading) {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const state = urlParams.get('state');

    void (async () => {
      if (!auth0.isAuthenticated && !(state === 'contacts')) {
        if (IS_ELECTRON) {
          await loginWithToken(true);
        } else {
          await auth0.loginWithRedirect({
            scope: AUTH0_MANAGEMENT_API_SCOPE,
          });
        }
        return;
      }

      await loginWithToken();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0, auth0.isLoading, loginWithToken]);

  if (IS_ELECTRON && isBlockingUpdateRequired()) {
    return (
      <CenteredLayout>
        <UpdateRequiredMessage />
      </CenteredLayout>
    );
  }

  const appIsStillLoading = () =>
    auth0.isLoading || !auth0.user || loading || personStore.isLoggingOut;

  if (appIsStillLoading() && shouldDisplayDesktopWelcome) {
    return <DesktopWelcome />;
  }

  if (appIsStillLoading()) {
    return <AppLoader />;
  }

  return error ? (
    <AppError error={error} />
  ) : (
    <AppWithErrorHandler
      ui={rootStore.uiStore}
      person={rootStore.personStore}
      message={rootStore.messageStore}
      phoneStore={rootStore.phoneStore}
    />
  );
};
